import { useExplainAction } from "@/api/genai";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/utils";
import { ErrorMsg, WorkingSignal } from "@cohesive/ui-components";
import {
  ArrowRight,
  RotateCcw,
  Send,
  ShieldAlert,
  Sparkles,
} from "lucide-react";
import {
  ACTION_EXPLANATION_SCHEMA,
  ActionExplanation,
  parseActionExplanation,
} from "./explanation";

/**
 * A reusable dialog that asks a fast model to explain an action in plain
 * language and renders it as scannable sections (summary, step-by-step, what it
 * touches, reversibility). Generic: callers pass the `inputText` prompt that
 * describes the action plus a stable `cacheKey` (e.g. a tool-call id). Built on
 * {@link useExplainAction}, so reopening reuses the cached result.
 */
export function ExplainActionDialog({
  open,
  onOpenChange,
  title = "What this will do",
  loadingText = "Reading the command…",
  inputText,
  cacheKey,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  loadingText?: string;
  inputText: string;
  cacheKey: string;
}) {
  const { data, isFetching, isError, refetch } = useExplainAction({
    cacheKey,
    inputText,
    schema: ACTION_EXPLANATION_SCHEMA,
    enabled: open,
  });

  const explanation = data?.structured_output
    ? parseActionExplanation(data.structured_output)
    : null;
  // A moderation flag or unparseable output is a soft failure — show the retry
  // affordance rather than a broken/empty body.
  const failed =
    isError || (!isFetching && !explanation) || data?.moderation_flagged;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">
            A plain-language explanation of what this action will do if
            approved.
          </DialogDescription>
        </DialogHeader>

        {isFetching && !explanation ? (
          <div className="py-2">
            <WorkingSignal variant="typewriter" text={loadingText} />
          </div>
        ) : failed || !explanation ? (
          <ErrorMsg
            title="Couldn't explain this"
            text="The explanation couldn't be generated. Try again."
            onRetry={() => refetch()}
          />
        ) : (
          <ExplanationBody explanation={explanation} />
        )}
      </DialogContent>
    </Dialog>
  );
}

const REVERSIBILITY: Record<
  ActionExplanation["reversibility"],
  { label: string; tone: string; Icon: typeof RotateCcw }
> = {
  reversible: {
    label: "Reversible",
    tone: "text-lc-active",
    Icon: RotateCcw,
  },
  "reversible-with-effort": {
    label: "Hard to undo",
    tone: "text-lc-waiting",
    Icon: ShieldAlert,
  },
  irreversible: {
    label: "Can't be undone",
    tone: "text-lc-blocked",
    Icon: ShieldAlert,
  },
};

function ExplanationBody({ explanation }: { explanation: ActionExplanation }) {
  const rev = REVERSIBILITY[explanation.reversibility];

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm leading-relaxed text-foreground">
        {explanation.summary}
      </p>

      {explanation.what_happens.length > 0 && (
        <Section label="Step by step">
          <ul className="flex flex-col gap-1.5">
            {explanation.what_happens.map((step, i) => (
              <li key={i} className="flex gap-2 text-sm text-foreground">
                <ArrowRight className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                <span>{step}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {explanation.data_touched.length > 0 && (
        <Section label="What it touches">
          <ul className="flex flex-col gap-1.5">
            {explanation.data_touched.map((item, i) => (
              <li key={i} className="flex gap-2 text-sm text-foreground">
                <Send className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <div className="flex items-start gap-2 rounded-lg border bg-muted/30 p-3">
        <rev.Icon className={cn("mt-0.5 size-4 shrink-0", rev.tone)} />
        <div className="flex flex-col gap-0.5">
          <span className={cn("text-xs font-medium", rev.tone)}>
            {rev.label}
          </span>
          {explanation.reversibility_note && (
            <span className="text-xs text-muted-foreground">
              {explanation.reversibility_note}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-start gap-2 border-t pt-3 text-xs text-muted-foreground">
        <Sparkles className="mt-0.5 size-3.5 shrink-0" />
        <span>
          AI-generated and may not be perfectly accurate. Review the command
          itself before approving.
        </span>
      </div>
    </div>
  );
}

function Section({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}
